import {
    Task,
    TaskGroupBy,
    TaskPriority,
    TaskStatus,
    UI_VISIBLE_TASK_STATUSES,
} from "@common/types/task";
import { Close } from "@common/schemas/close.schema";
import {
    startOfDay,
    differenceInDays,
    addDays,
    getDay,
    nextFriday,
    addWeeks,
    parseISO,
    formatISO,
} from "date-fns";

export const TASK_STATUS_MAP: Map<TaskStatus, string> = new Map([
    [TaskStatus.TODO, "To do"],
    [TaskStatus.IN_PROGRESS, "In progress"],
    [TaskStatus.DONE, "Done"],
    [TaskStatus.CANCELLED, "Canceled"],
]);

export const TASK_PRIORITY_MAP: Map<TaskPriority, string> = new Map([
    [TaskPriority.LOW, "Low"],
    [TaskPriority.MEDIUM, "Medium"],
    [TaskPriority.HIGH, "High"],
    [TaskPriority.URGENT, "Urgent"],
]);

export const NO_TASK_GROUP_KEY = "No task group";
export const NO_PRIORITY_KEY = "No priority";
export const NO_ASSIGNEE_KEY = "No assignee";

export enum DueDateGroup {
    OVERDUE = "overdue",
    TODAY = "today",
    TOMORROW = "tomorrow",
    THIS_WEEK = "this_week",
    NEXT_WEEK = "next_week",
    LATER = "later",
    NO_DUE_DATE = "no_due_date",
}

export const ORDERED_DUE_DATE_GROUPS: DueDateGroup[] = [
    DueDateGroup.OVERDUE,
    DueDateGroup.TODAY,
    DueDateGroup.TOMORROW,
    DueDateGroup.THIS_WEEK,
    DueDateGroup.NEXT_WEEK,
    DueDateGroup.LATER,
    DueDateGroup.NO_DUE_DATE,
];

export const DUE_DATE_GROUP_TITLES: Map<DueDateGroup, string> = new Map([
    [DueDateGroup.OVERDUE, "Overdue"],
    [DueDateGroup.TODAY, "Today"],
    [DueDateGroup.TOMORROW, "Tomorrow"],
    [DueDateGroup.THIS_WEEK, "This week"],
    [DueDateGroup.NEXT_WEEK, "Next week"],
    [DueDateGroup.LATER, "Later"],
    [DueDateGroup.NO_DUE_DATE, "No due date"],
]);

export const getDueDateGroup = (dueDate: string | null): DueDateGroup => {
    if (!dueDate) {
        return DueDateGroup.NO_DUE_DATE;
    }

    const today = startOfDay(new Date());
    const due = startOfDay(parseISO(dueDate));
    const diffDays = differenceInDays(due, today);

    // Overdue
    if (diffDays < 0) {
        return DueDateGroup.OVERDUE;
    }

    // Today
    if (diffDays === 0) {
        return DueDateGroup.TODAY;
    }

    // Tomorrow
    if (diffDays === 1) {
        return DueDateGroup.TOMORROW;
    }

    // This week (remaining days of current week)
    const dayOfWeek = getDay(today); // 0 = Sunday, 6 = Saturday
    const daysUntilEndOfWeek = 7 - dayOfWeek; // Days until Sunday
    if (diffDays <= daysUntilEndOfWeek) {
        return DueDateGroup.THIS_WEEK;
    }

    // Next week (next 7 days after this week ends)
    if (diffDays <= daysUntilEndOfWeek + 7) {
        return DueDateGroup.NEXT_WEEK;
    }

    // Later
    return DueDateGroup.LATER;
};

export const NO_CLOSE_STAGE_KEY = "No stage";
export const NO_CLOSE_KEY = "No service";

export const getTaskGroupId = (groupBy: TaskGroupBy, task: Task): string => {
    switch (groupBy) {
        case TaskGroupBy.DUE_DATE:
            return getDueDateGroup(task.dueDate);
        case TaskGroupBy.STATUS:
            return task.status;
        case TaskGroupBy.PRIORITY:
            return task.priority ?? NO_PRIORITY_KEY;
        case TaskGroupBy.ASSIGNEE:
            return task.assignee?.id.toString() ?? NO_ASSIGNEE_KEY;
        case TaskGroupBy.TASK_GROUP:
            return task.groupId ?? NO_TASK_GROUP_KEY;
        case TaskGroupBy.CLOSE_STAGE:
            return task.closeStageId ?? NO_CLOSE_STAGE_KEY;
        case TaskGroupBy.CLOSE:
            return task.closeId ?? NO_CLOSE_KEY;
        case TaskGroupBy.CLIENT:
            return task.workspaceId;
    }
};

export const getDefaultDueDateForGroup = (
    dueDateGroup: DueDateGroup,
): string | null => {
    const today = startOfDay(new Date());

    switch (dueDateGroup) {
        case DueDateGroup.OVERDUE:
            // Skip for overdue - no default date
            return null;
        case DueDateGroup.TODAY:
            // Set to current day
            return formatISO(today, { representation: "date" });
        case DueDateGroup.TOMORROW: {
            // Set to tomorrow
            const tomorrow = addDays(today, 1);
            return formatISO(tomorrow, { representation: "date" });
        }
        case DueDateGroup.THIS_WEEK: {
            // Set to Friday of current calendar week
            const dayOfWeek = getDay(today); // 0 = Sunday, 6 = Saturday
            // Calculate days until Friday (5)
            let daysUntilFriday = 5 - dayOfWeek;
            if (daysUntilFriday < 0) {
                // If we're past Friday (Saturday), go to next Friday
                daysUntilFriday += 7;
            }
            const friday = addDays(today, daysUntilFriday);
            return formatISO(friday, { representation: "date" });
        }
        case DueDateGroup.NEXT_WEEK: {
            // Set to Friday of next calendar week
            const thisWeekFriday = nextFriday(today);
            const nextWeekFriday = addWeeks(thisWeekFriday, 1);
            return formatISO(nextWeekFriday, { representation: "date" });
        }
        case DueDateGroup.LATER: {
            // Set to +15 days
            const later = addDays(today, 15);
            return formatISO(later, { representation: "date" });
        }
        case DueDateGroup.NO_DUE_DATE:
            return null;
    }
};

export const getAllPossibleGroupKeys = ({
    groupBy,
    statusFilter,
    workspaceMemberIds,
    taskGroupIds,
    closeStageIds,
    closeIds,
    clientsMap,
}: {
    groupBy: TaskGroupBy;
    statusFilter?: TaskStatus[];
    workspaceMemberIds?: number[];
    taskGroupIds?: string[];
    closeStageIds?: string[];
    closeIds?: string[];
    clientsMap?: ReadonlyMap<string, string>;
}): string[] => {
    switch (groupBy) {
        case TaskGroupBy.STATUS:
            // For status, only show groups that are in the filter
            // Filter out BLOCKED since it's been removed from the UI
            return (
                statusFilter?.filter((s) => s !== TaskStatus.BLOCKED) ?? [
                    ...UI_VISIBLE_TASK_STATUSES,
                ]
            );
        case TaskGroupBy.PRIORITY:
            // Show all priority levels + "No priority"
            return [...Object.values(TaskPriority), NO_PRIORITY_KEY];
        case TaskGroupBy.DUE_DATE:
            // Show all due date groups
            return Object.values(DueDateGroup);
        case TaskGroupBy.ASSIGNEE:
            // Show all workspace members + "No assignee"
            return [
                ...(workspaceMemberIds?.map((id) => id.toString()) ?? []),
                NO_ASSIGNEE_KEY,
            ];
        case TaskGroupBy.TASK_GROUP:
            return [...(taskGroupIds ?? []), NO_TASK_GROUP_KEY];
        case TaskGroupBy.CLOSE_STAGE:
            return [...(closeStageIds ?? []), NO_CLOSE_STAGE_KEY];
        case TaskGroupBy.CLOSE:
            return [...(closeIds ?? []), NO_CLOSE_KEY];
        case TaskGroupBy.CLIENT:
            return [...(clientsMap ?? [])]
                .toSorted(([, left], [, right]) => left.localeCompare(right))
                .map(([id]) => id);
    }
};

/** Groupings whose empty groups never show, in List groups and Board columns. */
export const shouldHideEmptyTaskGroups = (groupBy: TaskGroupBy): boolean =>
    groupBy === TaskGroupBy.CLOSE ||
    groupBy === TaskGroupBy.CLOSE_STAGE ||
    groupBy === TaskGroupBy.CLIENT;

export const getGroupTitleForKey = ({
    groupBy,
    groupKey,
    workspaceMembersMap,
    taskGroupsMap,
    closeStagesMap,
    closesMap,
    clientsMap,
}: {
    groupBy: TaskGroupBy;
    groupKey: string;
    workspaceMembersMap?: Map<number, string>;
    taskGroupsMap?: Map<string, string>;
    closeStagesMap?: Map<string, string>;
    closesMap?: Map<string, Close>;
    clientsMap?: ReadonlyMap<string, string>;
}): string => {
    switch (groupBy) {
        case TaskGroupBy.STATUS:
            return TASK_STATUS_MAP.get(groupKey as TaskStatus) ?? groupKey;
        case TaskGroupBy.PRIORITY:
            return TASK_PRIORITY_MAP.get(groupKey as TaskPriority) ?? groupKey;
        case TaskGroupBy.DUE_DATE:
            return (
                DUE_DATE_GROUP_TITLES.get(groupKey as DueDateGroup) ?? groupKey
            );
        case TaskGroupBy.ASSIGNEE: {
            if (groupKey === NO_ASSIGNEE_KEY) {
                return NO_ASSIGNEE_KEY;
            }
            const memberId = parseInt(groupKey, 10);
            return workspaceMembersMap?.get(memberId) ?? groupKey;
        }
        case TaskGroupBy.TASK_GROUP:
            return taskGroupsMap?.get(groupKey) ?? groupKey;
        case TaskGroupBy.CLOSE_STAGE:
            return closeStagesMap?.get(groupKey) ?? groupKey;
        case TaskGroupBy.CLOSE:
            return closesMap?.get(groupKey)?.name ?? groupKey;
        case TaskGroupBy.CLIENT:
            return clientsMap?.get(groupKey) ?? groupKey;
    }
};
